import React, { useState, useEffect, useRef } from 'react';

const FuseListItem = ({
  fuse,
  fetchFuse,
  handleOpenModal,
  isSelected,
  onSelectChange,
  renameFuse
}) => {
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [scoreClass, setScoreClass] = useState('fuse-number');
  const [scoreDisplay, setScoreDisplay] = useState('0%');
  const [renameInputVisible, setRenameInputVisible] = useState(false);
  const [renameInput, setRenameInput] = useState('');

  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const renameInputRef = useRef(null);

  const handleDropdownToggle = () => {
    setIsDropdownVisible((prevState) => !prevState);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isDropdownVisible &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsDropdownVisible(false);
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isDropdownVisible]);

  // Close rename input when clicking outside
  useEffect(() => {
    const handleClickOutsideInput = (event) => {
      if (
        renameInputVisible &&
        renameInputRef.current &&
        !renameInputRef.current.contains(event.target)
      ) {
        setRenameInputVisible(false);
        setRenameInput('');
      }
    };

    document.addEventListener('mousedown', handleClickOutsideInput);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideInput);
    };
  }, [renameInputVisible]);

  // Focus on rename input when it becomes visible
  useEffect(() => {
    if (renameInputVisible && renameInputRef.current) {
      renameInputRef.current.focus();
    }
  }, [renameInputVisible]);

  // Date formatting
  const formattedDate = fuse.post_date
    ? new Date(fuse.post_date).toLocaleDateString('en-US')
    : 'N/A';

  // Calculate ignition thrust
  useEffect(() => {
    const totalBars = 24;

    // Safely access 'Scale Ceiling' using optional chaining and default value
    const scaleCeilingValue = fuse.ignition_context?.['Scale Ceiling'];
    const scaleCeiling = parseFloat(scaleCeilingValue) || 100; // Default to 100 if undefined or invalid

    // Ensure 'ignition_thrust' is a valid number
    const ignitionThrust = parseFloat(fuse.ignition_thrust) || 0;
    const scoreNumber = ignitionThrust * 100;
    const scorePercentage = (scoreNumber * 100) / scaleCeiling;
    const percentageIndex = Math.ceil((scorePercentage * totalBars) / 100);

    // Determine score display
    if (scoreNumber > 3000) {
      setScoreDisplay('30X+');
    } else if (scoreNumber > 500) {
      setScoreDisplay(`${(scoreNumber / 100).toFixed(1)}X`);
    } else {
      setScoreDisplay(`${Math.round(scoreNumber)}%`);
    }

    // Determine score class based on percentage index
    if (percentageIndex < 6) {
      setScoreClass('fuse-number low');
    } else if (percentageIndex < 13) {
      setScoreClass('fuse-number mid-low');
    } else if (percentageIndex < 20) {
      setScoreClass('fuse-number mid-high');
    } else {
      setScoreClass('fuse-number high');
    }
  }, [fuse.ignition_thrust, fuse.ignition_context]);

  // Action handlers
  const handleOpenRename = () => {
    setIsDropdownVisible(false);
    setRenameInputVisible(true);
  };

  const handleShare = () => {
    handleOpenModal('share', fuse);
    setIsDropdownVisible(false);
  };

  const handleDelete = () => {
    handleOpenModal('deleteFuse', fuse);
    setIsDropdownVisible(false);
  };

  const handleRenameFuseTitle = () => {
    if (renameInput.trim() === '') {
      // Optionally handle empty input
      return;
    }
    fuse.fuse_title = renameInput;
    setRenameInputVisible(false);
    setRenameInput('');
    renameFuse(fuse.ID, renameInput);
  };

  return (
    <div
      className={`fuselight-dashboard-list-item ${
        fuse.isDeleting ? 'fade-out' : ''
      }`}
    >
      <div className="fuselight-dashboard-item-checkbox">
        <label className="custom-checkbox">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onSelectChange}
            aria-label={`Select Fusion ${fuse.brand_name}`}
          />
          <span></span>
        </label>
      </div>
      {renameInputVisible && (
        <input
          className="rename-input"
          type="text"
          ref={renameInputRef}
          value={renameInput}
          onChange={(e) => setRenameInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleRenameFuseTitle();
            }
          }}
        />
      )}
      <div
        className="project-info mobile-buttons-info"
        onClick={() => fetchFuse(fuse.ID)}
      >
        <p>{fuse.fuse_title ? fuse.fuse_title : fuse.brand_name || 'N/A'}</p>
        <p className="mobile-hide">
          {fuse.brand_name || 'N/A'} + {fuse.celebrity_name || 'N/A'}
        </p>
        <p>
          <span className={scoreClass}>{scoreDisplay}</span>
        </p>
        <p>{formattedDate}</p>
      </div>
      <div className="dropdown-container">
        <button className="dots" onClick={handleDropdownToggle} ref={buttonRef}>
          <div></div>
          <div></div>
          <div></div>
        </button>
        {isDropdownVisible && (
          <div className="dropdown-menu" ref={dropdownRef}>
            <button className="dropdown-item" onClick={handleOpenRename}>
              Rename
            </button>
            <button className="dropdown-item" onClick={handleShare}>
              Share
            </button>
            <button className="dropdown-item" onClick={handleDelete}>
              Delete
            </button>
          </div>
        )}
      </div>
      <div className="mobile-item-buttons">
        <button className="dropdown-item" onClick={handleShare}>
          Share
        </button>
      </div>
    </div>
  );
};

export default FuseListItem;
